import { type SiteId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { editEventTemplates } from '@solinkcorp/event-standard';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import { useMemo } from 'react';
import { ALARMS_URL } from 'utils/urls';
import { DEFAULT_VERIFICATION_WORKFLOW_ID } from 'vd-constants';

export interface UseAlarmEventsFilterOptions {
  route: string;
  filterStoreLocationIds?: readonly SiteId[];
  firstFilterStoreLocationId?: string;
}

/**
 * Hook to generate alarm events filter based on user verification workflow and route.
 * Handles filtering for verifier assignment and verification state based on the current route.
 */
export function useAlarmEventsFilter({
  route,
  filterStoreLocationIds,
  firstFilterStoreLocationId,
}: UseAlarmEventsFilterOptions) {
  const user = useUserSession();

  const openAlarmEventsFilter = useMemo(() => {
    const {
      profile: { name, email },
    } = user;

    const alarmTemplate =
      editEventTemplates[
        user.profile.metadata.verifEyeWorkflowId || DEFAULT_VERIFICATION_WORKFLOW_ID
      ];
    const templateStates = Object.keys(alarmTemplate.nodes);

    const verifierFilter: Record<string, any> = {};

    // Filter to view open alarms with no verification started (or this user started verification)
    verifierFilter['details.verifier'] = [`${name} (${email})`, null];

    // Filter dispatched alarms. There is no binary 'not' operator for the filter
    // so we'll use the possible states
    if (route === `${ALARMS_URL}/new/dispatched`) {
      verifierFilter['details.verificationState'] = 'done';
    } else {
      verifierFilter['details.verificationState'] = [
        null,
        ...templateStates.filter((state) => state !== 'done'),
      ];
    }

    // Add location filtering if provided
    if (filterStoreLocationIds && firstFilterStoreLocationId !== ALL_LOCATIONS) {
      return {
        ...verifierFilter,
        siteId: filterStoreLocationIds,
      };
    }

    return verifierFilter;
  }, [user, route, filterStoreLocationIds, firstFilterStoreLocationId]);

  return openAlarmEventsFilter;
}
